import { Capacitor } from "@capacitor/core"
import { SecureStoragePlugin } from "capacitor-secure-storage-plugin"
import { assertSessionVersion, getSessionVersion } from "./session-version"

type Key = "access_token" | "refresh_token" | "owner_id" | "user_id"

const KEYS: Key[] = ["access_token", "refresh_token", "owner_id", "user_id"]

function isNative(): boolean {
	return Capacitor.isNativePlatform()
}

async function nativeGet(key: Key): Promise<string | null> {
	try {
		const { value } = await SecureStoragePlugin.get({ key })
		return value
	} catch {
		// Key not found throws on iOS/Android
		return null
	}
}

async function nativeSet(key: Key, value: string): Promise<void> {
	await SecureStoragePlugin.set({ key, value })
}

async function nativeRemove(key: Key): Promise<void> {
	try {
		await SecureStoragePlugin.remove({ key })
	} catch {
		// Ignore if key doesn't exist
	}
}

/**
 * Единое хранилище учётных данных. Очередь сериализует ВСЕ операции:
 * загрузка из storage никогда не выполняется одновременно с записью и не
 * затирает свежие значения; мутации не пересекаются друг с другом.
 */
class CredentialStorage {
	private cache: Record<Key, string | null> = {
		access_token: null,
		refresh_token: null,
		owner_id: null,
		user_id: null,
	}
	private loaded = false
	private queue: Promise<unknown> = Promise.resolve()

	get(key: Key): Promise<string | null> {
		return this.enqueue(async () => {
			await this.ensureLoaded()
			return this.cache[key]
		})
	}

	set(
		values: Partial<Record<Key, string>>,
		version = getSessionVersion(),
	): Promise<void> {
		const entries: Array<[Key, string]> = []
		for (const key of KEYS) {
			const value = values[key]
			if (value !== undefined) entries.push([key, value])
		}
		return this.enqueue(async () => {
			await this.ensureLoaded()
			assertSessionVersion(version)
			if (isNative()) {
				await Promise.all(entries.map(([key, value]) => nativeSet(key, value)))
			} else {
				for (const [key, value] of entries) localStorage.setItem(key, value)
			}
			assertSessionVersion(version)
			for (const [key, value] of entries) this.cache[key] = value
		})
	}

	remove(keys: Key[]): Promise<void> {
		return this.enqueue(async () => {
			for (const key of keys) this.cache[key] = null
			this.loaded = true
			if (isNative()) {
				await Promise.all(keys.map((key) => nativeRemove(key)))
				return
			}
			const errors: unknown[] = []
			for (const key of keys) {
				try {
					localStorage.removeItem(key)
				} catch (error) {
					errors.push(error)
				}
			}
			if (errors.length)
				throw new AggregateError(errors, "Credential removal failed")
		})
	}

	private enqueue<T>(fn: () => Promise<T>): Promise<T> {
		// Запускаем операцию после того, как отстоится очередь (и на успех, и на ошибку).
		const operation = this.queue.then(fn, fn)
		// Сдвигаем очередь за эту операцию, поглощая её результат/ошибку,
		// чтобы следующая всегда стартовала.
		this.queue = operation.then(
			() => undefined,
			() => undefined,
		)
		return operation
	}

	/**
	 * Вызывается ТОЛЬКО внутри `enqueue`, поэтому не требует собственного
	 * промиса-замка. При ошибке `loaded` остаётся false — следующая операция
	 * повторит загрузку (нет залипания на rejected).
	 */
	private async ensureLoaded(): Promise<void> {
		if (this.loaded) return
		let values: (string | null)[]
		if (isNative()) values = await Promise.all(KEYS.map(nativeGet))
		else values = KEYS.map((key) => localStorage.getItem(key))
		KEYS.forEach((key, index) => {
			this.cache[key] = values[index] ?? null
		})
		this.loaded = true
	}
}

export const credentialStorage = new CredentialStorage()
